import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";

import {
  ArrowUpOutlined,
  DollarOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TransactionOutlined,
} from "@ant-design/icons";

import { PDFDownloadLink } from "@react-pdf/renderer";

import api from "../../../api/api";
import SalesRecordPDF from "./SalesRecordPDF";

const { Title, Text } = Typography;

const PURCHASES_ENDPOINT = "/api/purchases/";

const toNumber = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return 0;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
};

const money = (value) => {
  return `$${toNumber(value).toFixed(2)}`;
};

const number = (value) => {
  return toNumber(value).toLocaleString();
};

const normalizeSale = (sale) => {
  const revenue = toNumber(
    sale?.revenue
  );

  const cost = toNumber(
    sale?.cost
  );

  const profit =
    sale?.profit !== undefined &&
    sale?.profit !== null
      ? toNumber(sale.profit)
      : revenue - cost;

  return {
    ...sale,

    id:
      sale?.id ??
      sale?.purchase_id ??
      sale?.transaction_id ??
      "-",

    productName:
      sale?.product_name ||
      sale?.productName ||
      sale?.product?.name ||
      sale?.product?.product_name ||
      "Unknown Product",

    quantityTaken: toNumber(
      sale?.quantity_taken ??
        sale?.quantityTaken ??
        sale?.quantity
    ),

    unitsSold: toNumber(
      sale?.units_sold ??
        sale?.unitsSold ??
        sale?.units
    ),

    sellingPriceAtSale: toNumber(
      sale?.selling_price_at_sale ??
        sale?.sale_price_at_sale ??
        sale?.sellingPriceAtSale ??
        sale?.selling_price ??
        sale?.sale_price
    ),

    costPriceAtSale: toNumber(
      sale?.cost_price_at_sale ??
        sale?.purchase_cost_at_sale ??
        sale?.costPriceAtSale ??
        sale?.purchase_cost ??
        sale?.cost_price
    ),

    revenue,
    cost,
    profit,

    createdAt:
      sale?.created_at ||
      sale?.createdAt ||
      sale?.date ||
      sale?.transaction_date ||
      null,

    paymentMethod:
      sale?.payment_method ||
      sale?.paymentMethod ||
      sale?.payment_type ||
      "cash",

    purchasedBy:
      sale?.purchased_by ||
      sale?.purchasedBy ||
      sale?.customer_name ||
      sale?.customer?.name ||
      "-",

    year:
      sale?.year ??
      sale?.academic_year ??
      sale?.year_id ??
      null,

    yearName:
      sale?.year_name ||
      sale?.yearName ||
      sale?.academic_year_name ||
      sale?.academic_year_label ||
      sale?.year?.name ||
      "-",
  };
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const formatDate = (value) => {
  const date = parseDate(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
};

const getDateKey = (value) => {
  const date = parseDate(value);

  if (!date) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatPaymentMethod = (value) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (letter) => letter.toUpperCase()
    );
};

const InstantProductTransaction = () => {
  const [sales, setSales] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [selectedDate, setSelectedDate] =
    useState("all");

  const [selectedYear, setSelectedYear] =
    useState("");

  const [
    messageApi,
    contextHolder,
  ] = message.useMessage();

  const getHeaders = useCallback(() => {
    const token =
      localStorage.getItem(
        "access_token"
      ) ||
      localStorage.getItem(
        "access"
      ) ||
      localStorage.getItem(
        "token"
      );

    if (!token) {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchSales = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const allRecords = [];

        let page = 1;

        let hasNextPage = true;

        while (hasNextPage) {
          const params = {
            page,
          };

          if (selectedYear) {
            params.year_id =
              selectedYear;
          }

          const response =
            await api.get(
              PURCHASES_ENDPOINT,
              {
                headers:
                  getHeaders(),
                params,
              }
            );

          const payload =
            response?.data;

          let records = [];

          if (
            Array.isArray(
              payload
            )
          ) {
            records = payload;

            hasNextPage = false;
          } else if (
            Array.isArray(
              payload?.results
            )
          ) {
            records =
              payload.results;

            if (payload.next) {
              page += 1;
            } else {
              hasNextPage = false;
            }
          } else if (
            Array.isArray(
              payload?.purchases
            )
          ) {
            records =
              payload.purchases;

            hasNextPage = false;
          } else if (
            Array.isArray(
              payload?.data
            )
          ) {
            records =
              payload.data;

            hasNextPage = false;
          } else {
            records = [];
            hasNextPage = false;
          }

          allRecords.push(
            ...records
          );

          if (page > 1000) {
            hasNextPage = false;
          }
        }

        const normalized =
          allRecords.map(
            normalizeSale
          );

        setSales(
          normalized
        );
      } catch (err) {
        console.error(
          "FETCH SALES ERROR:",
          err
        );

        const backendMessage =
          err?.response?.data
            ?.detail ||
          err?.response?.data
            ?.error ||
          err?.response?.data
            ?.message;

        const errorMessage =
          backendMessage ||
          (
            err?.response?.status ===
            401
              ? "Your session has expired. Please login again."
              : err?.response?.status ===
                403
                ? "You do not have permission to view sales transactions."
                : "Unable to load sales transactions."
          );

        setError(
          errorMessage
        );

        messageApi.error(
          errorMessage
        );
      } finally {
        setLoading(false);
      }
    },
    [
      getHeaders,
      selectedYear,
      messageApi,
    ]
  );

  useEffect(() => {
    fetchSales();
  }, [
    fetchSales,
  ]);

  const years = useMemo(() => {
    const map =
      new Map();

    sales.forEach(
      (sale) => {
        if (
          sale?.year !== null &&
          sale?.year !== undefined &&
          sale?.yearName &&
          sale.yearName !== "-"
        ) {
          map.set(
            String(
              sale.year
            ),
            sale.yearName
          );
        }
      }
    );

    return Array.from(
      map.entries()
    )
      .map(
        ([
          value,
          label,
        ]) => ({
          value,
          label,
        })
      )
      .sort(
        (a, b) =>
          String(
            b.label
          ).localeCompare(
            String(
              a.label
            )
          )
      );
  }, [
    sales,
  ]);

  const availableDates =
    useMemo(() => {
      const dates =
        new Map();

      sales.forEach(
        (sale) => {
          const key =
            getDateKey(
              sale.createdAt
            );

          if (!key) {
            return;
          }

          dates.set(
            key,
            formatDate(
              sale.createdAt
            )
          );
        }
      );

      return Array.from(
        dates.entries()
      ).sort(
        (a, b) =>
          b[0].localeCompare(
            a[0]
          )
      );
    }, [
      sales,
    ]);

  const filteredSales =
    useMemo(() => {
      const searchText =
        search
          .trim()
          .toLowerCase();

      return sales.filter(
        (sale) => {
          if (
            selectedDate !==
              "all" &&
            getDateKey(
              sale.createdAt
            ) !==
              selectedDate
          ) {
            return false;
          }

          if (!searchText) {
            return true;
          }

          const searchableValues = [
            sale.productName,
            sale.id,
            sale.paymentMethod,
            sale.purchasedBy,
            sale.yearName,
          ];

          return searchableValues.some(
            (value) =>
              String(
                value ?? ""
              )
                .toLowerCase()
                .includes(
                  searchText
                )
          );
        }
      );
    }, [
      sales,
      search,
      selectedDate,
    ]);

  const totals =
    useMemo(() => {
      return filteredSales.reduce(
        (
          acc,
          sale
        ) => {
          acc.quantity +=
            toNumber(
              sale.quantityTaken
            );

          acc.units +=
            toNumber(
              sale.unitsSold
            );

          acc.revenue +=
            toNumber(
              sale.revenue
            );

          acc.cost +=
            toNumber(
              sale.cost
            );

          acc.profit +=
            toNumber(
              sale.profit
            );

          return acc;
        },
        {
          quantity: 0,
          units: 0,
          revenue: 0,
          cost: 0,
          profit: 0,
        }
      );
    }, [
      filteredSales,
    ]);

  const selectedYearName =
    selectedYear
      ? (
          years.find(
            (year) =>
              String(
                year.value
              ) ===
              String(
                selectedYear
              )
          )?.label ||
          null
        )
      : null;

  const clearFilters = () => {
    setSearch("");
    setSelectedDate(
      "all"
    );
    setSelectedYear("");
  };

  const pdfFileName =
    selectedDate === "all"
      ? "sales-record.pdf"
      : `sales-record-${selectedDate}.pdf`;

  const columns = [
    {
      title: "#",
      key: "number",
      width: 60,
      fixed: "left",
      render: (
        _,
        __,
        index
      ) =>
        index + 1,
    },
    {
      title: "Product",
      dataIndex:
        "productName",
      key:
        "productName",
      width: 190,
      render: (
        value
      ) => (
        <Text strong>
          {value}
        </Text>
      ),
    },
    {
      title: "Date",
      dataIndex:
        "createdAt",
      key:
        "createdAt",
      width: 125,
      render: (
        value
      ) =>
        formatDate(
          value
        ),
    },
    {
      title: "Qty",
      dataIndex:
        "quantityTaken",
      key:
        "quantityTaken",
      width: 90,
      align: "right",
      render: (
        value
      ) =>
        number(
          value
        ),
    },
    {
      title: "Units",
      dataIndex:
        "unitsSold",
      key:
        "unitsSold",
      width: 90,
      align: "right",
      render: (
        value
      ) =>
        number(
          value
        ),
    },
    {
      title: "Revenue",
      dataIndex:
        "revenue",
      key:
        "revenue",
      width: 120,
      align: "right",
      render: (
        value
      ) =>
        money(
          value
        ),
    },
    {
      title: "Net Profit",
      dataIndex:
        "profit",
      key:
        "profit",
      width: 125,
      align: "right",
      render: (
        value
      ) => (
        <Text
          strong
          type={
            toNumber(
              value
            ) >= 0
              ? "success"
              : "danger"
          }
        >
          {money(
            value
          )}
        </Text>
      ),
    },
    {
      title: "Payment",
      dataIndex:
        "paymentMethod",
      key:
        "paymentMethod",
      width: 120,
      responsive: [
        "lg",
      ],
      render: (
        value
      ) => (
        <Tag>
          {formatPaymentMethod(
            value
          )}
        </Tag>
      ),
    },
    {
      title:
        "Purchased By",
      dataIndex:
        "purchasedBy",
      key:
        "purchasedBy",
      width: 150,
      responsive: [
        "xl",
      ],
      render: (
        value
      ) =>
        value || "-",
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        padding:
          "clamp(10px, 2vw, 24px)",
        boxSizing:
          "border-box",
      }}
    >
      {contextHolder}

      <Card
        bordered={false}
        style={{
          marginBottom: 16,
          borderRadius: 14,
        }}
      >
        <Row
          gutter={[
            12,
            12,
          ]}
          align="middle"
        >
          <Col
            xs={24}
            md={14}
          >
            <Title
              level={3}
              style={{
                margin: 0,
              }}
            >
              <TransactionOutlined />{" "}
              Sales Transactions
            </Title>

            <Text type="secondary">
              Backend-recorded sales,
              historical cost,
              revenue and profit.
            </Text>
          </Col>

          <Col
            xs={24}
            md={10}
            style={{
              textAlign:
                "right",
            }}
          >
            <Space wrap>
              <Button
                icon={
                  <ReloadOutlined />
                }
                onClick={
                  fetchSales
                }
                loading={
                  loading
                }
              >
                Refresh
              </Button>

              <PDFDownloadLink
                document={
                  <SalesRecordPDF
                    sales={
                      filteredSales
                    }
                    reportDate={
                      selectedDate ===
                      "all"
                        ? null
                        : selectedDate
                    }
                    yearName={
                      selectedYearName
                    }
                    totals={
                      totals
                    }
                  />
                }
                fileName={
                  pdfFileName
                }
              >
                {({
                  loading:
                    pdfLoading,
                }) => (
                  <Button
                    type="primary"
                    icon={
                      <FilePdfOutlined />
                    }
                    loading={
                      pdfLoading
                    }
                    disabled={
                      filteredSales.length ===
                      0
                    }
                  >
                    PDF
                  </Button>
                )}
              </PDFDownloadLink>
            </Space>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{
            marginBottom: 16,
          }}
        />
      )}

      <Card
        bordered={false}
        style={{
          marginBottom: 16,
          borderRadius: 14,
        }}
      >
        <Row
          gutter={[
            12,
            12,
          ]}
        >
          <Col
            xs={24}
            sm={12}
            md={8}
          >
            <Input
              allowClear
              prefix={
                <SearchOutlined />
              }
              placeholder="Search product, ID, payment..."
              value={
                search
              }
              onChange={(
                e
              ) =>
                setSearch(
                  e.target.value
                )
              }
            />
          </Col>

          <Col
            xs={24}
            sm={12}
            md={8}
          >
            <Select
              style={{
                width: "100%",
              }}
              value={
                selectedDate
              }
              onChange={
                setSelectedDate
              }
              showSearch
              optionFilterProp="label"
              placeholder="Select date"
              options={[
                {
                  value:
                    "all",
                  label:
                    "All Dates",
                },
                ...availableDates.map(
                  ([
                    value,
                    label,
                  ]) => ({
                    value,
                    label,
                  })
                ),
              ]}
            />
          </Col>

          <Col
            xs={24}
            sm={12}
            md={8}
          >
            <Select
              allowClear
              placeholder="Financial Year"
              style={{
                width: "100%",
              }}
              value={
                selectedYear ||
                undefined
              }
              onChange={(
                value
              ) => {
                setSelectedYear(
                  value || ""
                );

                setSelectedDate(
                  "all"
                );
              }}
              options={
                years
              }
              showSearch
              optionFilterProp="label"
            />
          </Col>

          {(search ||
            selectedDate !==
              "all" ||
            selectedYear) && (
            <Col xs={24}>
              <Button
                onClick={
                  clearFilters
                }
              >
                Clear Filters
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      <Row
        gutter={[
          12,
          12,
        ]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col
          xs={12}
          sm={12}
          md={6}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              height: "100%",
            }}
          >
            <Statistic
              title="Transactions"
              value={
                filteredSales.length
              }
              prefix={
                <ShoppingCartOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={12}
          sm={12}
          md={6}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              height: "100%",
            }}
          >
            <Statistic
              title="Units Sold"
              value={
                totals.units
              }
              prefix={
                <ArrowUpOutlined />
              }
            />
          </Card>
        </Col>

        <Col
          xs={12}
          sm={12}
          md={6}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              height: "100%",
            }}
          >
            <Statistic
              title="Revenue"
              value={
                totals.revenue
              }
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>

        <Col
          xs={12}
          sm={12}
          md={6}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              height: "100%",
            }}
          >
            <Statistic
              title="Cost of Sales"
              value={
                totals.cost
              }
              precision={2}
              prefix={
                <DollarOutlined />
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        bordered={false}
        style={{
          marginBottom: 16,
          borderRadius: 14,
        }}
      >
        <Statistic
          title="Net Profit"
          value={
            totals.profit
          }
          precision={2}
          prefix="$"
          valueStyle={{
            fontWeight: 700,
            fontSize:
              "clamp(25px, 5vw, 38px)",
          }}
        />

        <Text type="secondary">
          Revenue minus the
          historical backend cost
          of sales.
        </Text>
      </Card>

      <Card
        bordered={false}
        style={{
          borderRadius: 14,
          overflow: "hidden",
        }}
        bodyStyle={{
          padding: 0,
        }}
      >
        {loading ? (
          <div
            style={{
              padding: 50,
              textAlign:
                "center",
            }}
          >
            <Spin size="large" />

            <div
              style={{
                marginTop: 12,
              }}
            >
              Loading transactions...
            </div>
          </div>
        ) : filteredSales.length ===
          0 ? (
          <Empty
            description={
              sales.length === 0
                ? "No sales transactions found"
                : "No transactions match the current filters"
            }
            style={{
              padding: 50,
            }}
          />
        ) : (
          <Table
            rowKey={(
              record,
              index
            ) =>
              `${record.id}-${record.createdAt || "date"}-${index}`
            }
            columns={
              columns
            }
            dataSource={
              filteredSales
            }
            loading={
              loading
            }
            scroll={{
              x: 1300,
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger:
                true,
              pageSizeOptions: [
                10,
                20,
                50,
                100,
              ],
              showTotal: (
                total,
                range
              ) =>
                `${range[0]}-${range[1]} of ${total}`,
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default InstantProductTransaction;

